import "./debug-trap.ts";
import "../styles.css";
import { Elm } from "../elm/Main.elm";
import { registerMeditationAudio } from "./meditation-audio.ts";
import { registerMeditationStarfield } from "./meditation-starfield.ts";
import { registerVoiceClip } from "./voice-clip.ts";
import { registerBrassAstrolabe } from "./brass-astrolabe.ts";
import { registerBgRibbons } from "./bg-ribbons.ts";
import { registerBgNebula } from "./bg-nebula.ts";
import { registerBgRings } from "./bg-rings.ts";
import { registerBgConstellation } from "./bg-constellation.ts";
import { registerCompassNeedleParticles } from "./compass-needle-particles.ts";
import { registerNeedleTween } from "./needle-tween.ts";
import { wireClerkBridge } from "./clerk-bridge.ts";
import { wireStorageBridge } from "./storage-bridge.ts";
import { wireSentryBridge } from "./sentry-bridge.ts";
import { wireHapticsBridge } from "./haptics-bridge.ts";
import { wireJuiceAudio } from "./juice-audio.ts";
import { wireCheckoutBridge } from "./checkout-bridge.ts";
import { wireReferralBridge } from "./referral-bridge.ts";

const env = {
  clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? "",
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  audioBaseUrl: import.meta.env.VITE_AUDIO_BASE_URL ?? "/audio",
  env: import.meta.env.VITE_ENV ?? "development",
  // "stripe" only when the real Stripe pipe is wired and a publishable key
  // is set. Everything else is treated as stubbed — no real purchases can
  // happen, so Elm hard-blocks locked content rather than handing it out.
  checkoutMode:
    import.meta.env.VITE_CHECKOUT_MODE === "stripe" &&
    (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "") !== ""
      ? ("stripe" as const)
      : ("mock" as const),
} as const;

const mountNode = document.getElementById("elm-root");
if (mountNode === null) {
  throw new Error("Missing #elm-root mount node in index.html");
}

registerMeditationAudio();
registerMeditationStarfield();
registerVoiceClip();
registerBrassAstrolabe();
registerBgRibbons();
registerBgNebula();
registerBgRings();
registerBgConstellation();
registerCompassNeedleParticles();
registerNeedleTween();

const app = Elm.Main.init({
  node: mountNode,
  flags: {
    now: Date.now(),
    clerkPublishableKey: env.clerkPublishableKey,
    audioBaseUrl: env.audioBaseUrl,
    env: env.env,
    checkoutMode: env.checkoutMode,
  },
});

// Dev-only Msg log — wired FIRST so it catches every init-time Msg.
// In production this whole block is skipped (env === "production") so
// users never pay the subscribe cost or get console noise. Captured
// into a global ring buffer the headless E2E auditor reads after each
// scripted flow to verify the Msg sequence is what we expect.
if (env.env !== "production") {
  type MsgEntry = { readonly t: number; readonly msg: string };
  const log: MsgEntry[] = [];
  (globalThis as unknown as { __mc_msg_log__: readonly MsgEntry[] }).__mc_msg_log__ = log;
  app.ports.logMsg?.subscribe((msg) => {
    log.push({ t: performance.now(), msg });
    if (log.length > 4000) log.shift();
  });
}

wireSentryBridge(
  { dsn: env.sentryDsn, environment: env.env },
  { reportError: app.ports.reportError },
);

// Dev-only auth bypass for headless screenshots.
// Trigger with ?dev_auth=1 on a dev/preview build — sends a fake LoggedIn snapshot
// so /app/** routes render. No-op in production regardless of query string.
const devAuthRequested =
  env.env !== "production" &&
  new URLSearchParams(globalThis.location.search).get("dev_auth") === "1";

if (devAuthRequested) {
  queueMicrotask(() => {
    // Snapshot shape must match Service.ClerkService.decodeSnapshot:
    //   { user: { id, email, firstName? } | null, session: bool }
    // Sending `userId` here previously caused the decoder to fail, which
    // bounced the model into SessionLoggedOut → auth guard redirected
    // every /app navigation to /login. Headless screenshots of /app
    // showed Login content as a result.
    app.ports.clerkSub?.send({
      user: {
        id: "dev_user_screenshot",
        email: "screenshots@meditationcompass.au",
        firstName: "Captain",
      },
      session: true,
    });
  });
} else {
  await wireClerkBridge(env.clerkPublishableKey, {
    clerkCmd: app.ports.clerkCmd,
    clerkSub: app.ports.clerkSub,
    clerkEvent: app.ports.clerkEvent,
  });
}

wireStorageBridge({
  persistSession: app.ports.persistSession,
  loadSessions: app.ports.loadSessions,
  sessionsLoaded: app.ports.sessionsLoaded,
});

wireHapticsBridge({ haptic: app.ports.haptic });

wireJuiceAudio({ pluck: app.ports.pluck });

wireCheckoutBridge(app.ports);

wireReferralBridge(app.ports);

// Pull persisted sessions out of localStorage on first mount.
queueMicrotask(() => {
  // Prime sessions on first mount.
  const sessions = (() => {
    try {
      const raw = localStorage.getItem("mc.sessions.v1");
      return raw === null ? [] : JSON.parse(raw);
    } catch {
      return [];
    }
  })();
  app.ports.sessionsLoaded?.send(sessions);

  // Prime entitlement on first mount.
  const entitlement = (() => {
    try {
      const raw = localStorage.getItem("mc.entitlement.v1");
      return raw === null ? { kind: "free" } : JSON.parse(raw);
    } catch {
      return { kind: "free" };
    }
  })();
  app.ports.entitlementLoaded?.send(entitlement);
});

// Persist entitlement on every Elm-side write.
app.ports.persistEntitlement?.subscribe((value) => {
  try {
    localStorage.setItem("mc.entitlement.v1", JSON.stringify(value));
  } catch {
    /* quota — silent */
  }
});

// Soundtrack persistence — toggle + dual volumes survive reload.
const SOUNDTRACK_KEY = "mc.soundtrack.v1";
app.ports.persistSoundtrack?.subscribe((value) => {
  try {
    localStorage.setItem(SOUNDTRACK_KEY, JSON.stringify(value));
  } catch { /* quota */ }
});
queueMicrotask(() => {
  try {
    const raw = localStorage.getItem(SOUNDTRACK_KEY);
    if (raw === null) return;
    app.ports.soundtrackLoaded?.send(JSON.parse(raw));
  } catch { /* malformed */ }
});

// Compass guide — "have they seen the first-run dial instructions?".
// Device-local UI state, so localStorage rather than the account.
const GUIDE_KEY = "mc.guide.v1";
app.ports.persistGuide?.subscribe((value) => {
  try {
    localStorage.setItem(GUIDE_KEY, JSON.stringify(value));
  } catch { /* quota */ }
});
queueMicrotask(() => {
  // ALWAYS send, even when the key is absent — Elm holds the guide in
  // `GuideUnknown` (renders nothing) until this answers, so a missing
  // send would mean a first-time user never sees the guide at all.
  const guide = (() => {
    try {
      const raw = localStorage.getItem(GUIDE_KEY);
      return raw === null ? { seen: false } : JSON.parse(raw);
    } catch {
      return { seen: false };
    }
  })();
  app.ports.guideLoaded?.send(guide);
});

// Bridge the high-frequency window event into the Elm port.
window.addEventListener("mc:timeupdate", (event) => {
  const detail = (event as CustomEvent<{ id: string; position: number }>).detail;
  app.ports.audioTick?.send(detail);
});

// Continuity by colour — Elm writes the tapped meditation's accent hex to
// `--app-accent` on :root. Player surface + post-tap bloom both read this var
// so the dot you tapped on /app and the medallion you land on share the
// same hue. One perceived motion across the route change.
app.ports.setAccentColor?.subscribe((color: string) => {
  // Validate: only short hex / oklch / rgba allowed; anything else is dropped.
  // Prevents script-injection vectors via stringly-typed CSS values.
  const safe = /^(#[0-9a-fA-F]{3,8}|oklch\([^)]+\)|rgba?\([^)]+\))$/u.test(color);
  if (!safe) return;
  document.documentElement.style.setProperty("--app-accent", color);
});

// Register the service worker (production only — dev gets fresh code every save).
if ("serviceWorker" in navigator && env.env !== "development") {
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err: unknown) => {
        console.warn("[sw] registration failed", err);
      });
  });
}

